export const OTP_RESEND_SECONDS = 60;
export const OTP_MAX_SENDS = 3;
export const OTP_LOCK_MINUTES = 30;

export type OtpGuardRecord = {
  sentAt: number[];
  blockedUntil: number | null;
};

export type OtpGuardStatus = {
  allowed: boolean;
  waitSeconds: number;
  attemptsRemaining: number;
  reason: "ready" | "resend" | "locked";
};

export const emptyOtpGuard = (): OtpGuardRecord => ({
  sentAt: [],
  blockedUntil: null,
});

export function normalizeOtpGuard(
  record: OtpGuardRecord | null,
  now = Date.now(),
): OtpGuardRecord {
  if (!record) return emptyOtpGuard();
  if (record.blockedUntil && record.blockedUntil <= now) return emptyOtpGuard();
  return {
    sentAt: record.sentAt.filter(
      (sentAt) => now - sentAt < OTP_LOCK_MINUTES * 60_000,
    ),
    blockedUntil: record.blockedUntil,
  };
}

export function getOtpGuardStatus(
  record: OtpGuardRecord | null,
  now = Date.now(),
): OtpGuardStatus {
  const current = normalizeOtpGuard(record, now);
  if (current.blockedUntil && current.blockedUntil > now) {
    return {
      allowed: false,
      waitSeconds: Math.ceil((current.blockedUntil - now) / 1000),
      attemptsRemaining: 0,
      reason: "locked",
    };
  }
  const lastSend = current.sentAt.at(-1);
  if (lastSend !== undefined && now - lastSend < OTP_RESEND_SECONDS * 1000) {
    return {
      allowed: false,
      waitSeconds: Math.ceil(
        (OTP_RESEND_SECONDS * 1000 - (now - lastSend)) / 1000,
      ),
      attemptsRemaining: Math.max(OTP_MAX_SENDS - current.sentAt.length, 0),
      reason: "resend",
    };
  }
  return {
    allowed: true,
    waitSeconds: 0,
    attemptsRemaining: Math.max(OTP_MAX_SENDS - current.sentAt.length, 0),
    reason: "ready",
  };
}

export function recordOtpSend(
  record: OtpGuardRecord | null,
  now = Date.now(),
): OtpGuardRecord {
  const current = normalizeOtpGuard(record, now);
  const sentAt = [...current.sentAt, now].slice(-OTP_MAX_SENDS);
  return {
    sentAt,
    blockedUntil:
      sentAt.length >= OTP_MAX_SENDS
        ? now + OTP_LOCK_MINUTES * 60_000
        : current.blockedUntil,
  };
}

export function formatOtpWait(waitSeconds: number) {
  const minutes = Math.floor(waitSeconds / 60);
  const seconds = waitSeconds % 60;
  return minutes
    ? `${minutes}:${String(seconds).padStart(2, "0")}`
    : `${seconds}s`;
}
